import React, { useEffect, useState } from 'react';
import Modal from '@/Components/Modal';
import Spinner from '@/Components/Spinner';
import PrimaryButton from '@/Components/PrimaryButton';
import FontPicker from '@/Components/FontPicker';
import MaskingPreview from '@/Pages/Subtitle/Partials/MaskingPreview';
import { Subtitle, SubtitleStyle } from '@/models/Subtitle';
import { getOriginalSubtitleBounds } from '@/services/subtitleService';
import { generateMaskingStyles } from '@/services/maskingService';
import { getDefaultFont } from '@/services/googleFontsService';
import { Tooltip } from 'flowbite-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

interface MaskingModalProps {
    show: boolean;
    jobId: number;
    items: Subtitle[];
    availableFonts: string[];
    videoWidth: number;
    videoHeight: number;
    isOriginalLanguage: boolean;
    onComplete: (updatedItems: Subtitle[]) => void;
    onClose: () => void;
}

const MaskingModal: React.FC<MaskingModalProps> = ({
    show,
    jobId,
    items,
    availableFonts,
    videoWidth,
    videoHeight,
    isOriginalLanguage,
    onComplete,
    onClose,
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedFont, setSelectedFont] = useState<string>('');
    const [backgroundMode, setBackgroundMode] = useState<SubtitleStyle['backgroundMode']>('per-line');

    useEffect(() => {
        if (show) {
            setSelectedFont(getDefaultFont(availableFonts));
        }
    }, [show, items, availableFonts]);

    useEffect(() => {
        if (!show) {
            setTimeout(() => {
                setIsProcessing(false);
                setError(null);
            }, 300);
        }
    }, [show]);

    const handleGenerate = async () => {
        setIsProcessing(true);
        setError(null);

        try {
            const originalBounds = await getOriginalSubtitleBounds(jobId);

            if (!originalBounds || originalBounds.length === 0) {
                throw new Error('No original subtitle bounds found. This feature requires the original video subtitles.');
            }

            const result = await generateMaskingStyles(
                items,
                originalBounds,
                videoWidth,
                videoHeight,
                selectedFont,
                backgroundMode
            );

            onComplete(result.updatedSubtitles);
        } catch (err: any) {
            setError(err.message || 'Failed to generate masking styles');
        }
    };

    const isSetup = !isProcessing && !error;

    return (
        <Modal show={show} onClose={onClose} maxWidth="sm">
            <div className="p-6">
                <div className="relative mb-4">
                    <h2 className="px-8 text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Auto-Mask Subtitles
                    </h2>
                    <button
                        onClick={onClose}
                        className="absolute -end-1 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        title="Close"
                    >
                        <FontAwesomeIcon icon={faXmark} className="w-4 h-4"/>
                    </button>
                </div>

                {isSetup ? (
                    <>
                        <MaskingPreview backgroundMode={backgroundMode}/>
                        <p className="text-sm text-gray-600 dark:text-gray-200 my-5">
                            Auto-masking will generate styles to cover the original subtitle texts with your translated subtitles.
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                Select Font
                            </label>
                            <FontPicker
                                height={'40px'}
                                value={selectedFont}
                                onChange={setSelectedFont}
                                availableFonts={availableFonts}
                            />
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                Background Style
                            </label>
                            <div className="flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
                                {([['per-line', 'Per Line'], ['unified', 'Unified']] as const).map(([mode, label]) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => setBackgroundMode(mode)}
                                        className={`flex-1 rounded-md py-1.5 text-sm transition-colors ${
                                            backgroundMode === mode
                                                ? 'bg-white dark:bg-gray-600 font-medium text-gray-900 dark:text-white shadow-sm'
                                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <Tooltip
                            content="Translate your subtitles first to use this feature"
                            className={`${isOriginalLanguage ? '' : 'hidden'} p-2 py-1 text-xs`}
                            theme={{target: 'w-full'}}
                        >
                            <PrimaryButton
                                className="w-full"
                                onClick={handleGenerate}
                                disabled={isOriginalLanguage}
                            >
                                Generate
                            </PrimaryButton>
                        </Tooltip>
                    </>
                ) : error ? (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-32">
                        <Spinner size={10}/>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default MaskingModal;
